import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { MONTH_NAMES, formatDate, formatDateTime, formatMoney } from '../lib/format';
import { useI18n, type TranslationKey } from '../lib/i18n';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Select,
  Table,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';
import { useClientName, useExternalSystemOptions, useSubscriptionProjects, type Cycle } from './Subscriptions';

/**
 * Subscriptions in figures, and their billing (2026-10-28; spec §13, §6.2).
 * Contracted recurring revenue, what was invoiced and what was collected are
 * three different things, and each is labelled as what it is.
 */

interface Overview {
  asOf: string;
  range: { from: string; to: string };
  counts: { total: number; active: number; pastDue: number; cancelled: number; ending: number; monthly: number; yearly: number };
  recurring: {
    byCurrency: Array<{ currency: string; subscriptions: number; mrr: number; arr: number }>;
    base: { currency: string; mrr: number; arr: number; unconvertedCurrencies: string[] };
  };
  byProject: Array<{ id: string; code: string; name: string; nameAr: string | null; live: number; mrr: Array<{ currency: string; mrr: number }> }>;
  byPackage: Array<{ id: string; code: string; name: string; nameAr: string | null; project: string; live: number; mrr: Array<{ currency: string; mrr: number }> }>;
  trend: Array<{ month: string; started: number; cancelled: number }>;
  billing: {
    byCurrency: Array<{ currency: string; invoiced: number; invoices: number; collected: number; payments: number; outstanding: number }>;
    collectedBase: { currency: string; amount: number };
  };
  aging: Array<{ currency: string } & Record<'current' | 'd1_30' | 'd31_60' | 'd61_90' | 'd90_plus', { amount: number; invoices: number }>>;
  upcoming: Array<{
    id: string;
    number: string;
    nextBillingDate: string | null;
    billingCycle: Cycle;
    currency: string;
    netPrice: number;
    client: { id: string; code: string; companyName: string; companyNameAr: string | null };
    package: { name: string; nameAr: string | null };
  }>;
}

const AGING_KEYS = ['current', 'd1_30', 'd31_60', 'd61_90', 'd90_plus'] as const;

export function SubscriptionsOverviewPage() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const clientName = useClientName();
  const projects = useSubscriptionProjects();
  const systems = useExternalSystemOptions();
  const [filters, setFilters] = useState<Record<string, string>>({});
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscriptions', 'overview', filters],
    queryFn: () => api.get<Overview>('/subscriptions/overview', filters),
  });
  const filter = (key: string, value: string) => setFilters({ ...filters, [key]: value });
  const money = (n: number, currency: string) => formatMoney(n, currency, locale);
  const monthLabel = (month: string) => {
    const [y, m] = month.split('-').map(Number);
    return `${MONTH_NAMES[locale][m - 1]} ${y}`;
  };

  return (
    <>
      <PageHeader title={t('subs.overview.title')} subtitle={t('subs.overview.subtitle')} />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <Select value={filters.projectId ?? ''} onChange={(e) => filter('projectId', e.target.value)} aria-label={t('subs.product')}>
            <option value="">{t('subs.product')} — {t('common.all')}</option>
            {(projects.data ?? []).filter((p) => p.offered || p.liveSubscriptions > 0).map((p) => (
              <option key={p.id} value={p.id}>{localized(p)}</option>
            ))}
          </Select>
          <Select value={filters.externalSystemId ?? ''} onChange={(e) => filter('externalSystemId', e.target.value)} aria-label={t('subs.system')}>
            <option value="">{t('subs.system')} — {t('common.all')}</option>
            {(systems.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
          <Input
            placeholder={t('common.currency')}
            aria-label={t('common.currency')}
            maxLength={3}
            value={filters.currency ?? ''}
            onChange={(e) => filter('currency', e.target.value.toUpperCase())}
          />
          <Input type="date" aria-label={t('common.from')} title={t('common.from')} value={filters.from ?? ''} onChange={(e) => filter('from', e.target.value)} />
          <Input type="date" aria-label={t('common.to')} title={t('common.to')} value={filters.to ?? ''} onChange={(e) => filter('to', e.target.value)} />
        </div>
      </Card>

      {isLoading ? (
        <LoadingBlock />
      ) : error || !data ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className="grid gap-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label={t('subs.kpi.active')} value={data.counts.active} tone="success" onClick={() => navigate('/subscriptions?status=ACTIVE')} />
            <Metric label={t('subs.kpi.pastDue')} value={data.counts.pastDue} tone={data.counts.pastDue ? 'warning' : 'neutral'} />
            <Metric label={t('subs.kpi.ending')} value={data.counts.ending} hint={t('subs.kpi.endingHint')} />
            <Metric label={t('subs.kpi.cancelled')} value={data.counts.cancelled} />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Metric
              label={t('subs.kpi.mrr')}
              tone="brand"
              value={money(data.recurring.base.mrr, data.recurring.base.currency)}
              hint={t('subs.kpi.inBase', { currency: data.recurring.base.currency })}
            />
            <Metric
              label={t('subs.kpi.arr')}
              tone="brand"
              value={money(data.recurring.base.arr, data.recurring.base.currency)}
              hint={
                data.recurring.base.unconvertedCurrencies.length
                  ? t('subs.kpi.unconverted', { list: data.recurring.base.unconvertedCurrencies.join(', ') })
                  : t('subs.kpi.mrrHint')
              }
            />
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <Card padded={false}>
              <CardHeader title={t('subs.recurringByCurrency')} subtitle={t('subs.kpi.mrrHint')} className="p-5 pb-0" />
              {data.recurring.byCurrency.length === 0 ? (
                <EmptyState />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('common.currency')}</Th>
                      <Th align="end">{t('subs.live')}</Th>
                      <Th align="end">{t('subs.kpi.mrr')}</Th>
                      <Th align="end">{t('subs.kpi.arr')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recurring.byCurrency.map((r) => (
                      <Tr key={r.currency}>
                        <Td>{r.currency}</Td>
                        <Td align="end" className="tabular">{r.subscriptions}</Td>
                        <Td align="end" className="tabular">{money(r.mrr, r.currency)}</Td>
                        <Td align="end" className="tabular">{money(r.arr, r.currency)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>

            <Card padded={false}>
              <CardHeader
                title={t('subs.billingRange', { from: formatDate(data.range.from, locale), to: formatDate(data.range.to, locale) })}
                subtitle={t('subs.collectedBase', {
                  currency: data.billing.collectedBase.currency,
                  amount: money(data.billing.collectedBase.amount, data.billing.collectedBase.currency),
                })}
                className="p-5 pb-0"
              />
              {data.billing.byCurrency.length === 0 ? (
                <EmptyState />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('common.currency')}</Th>
                      <Th align="end">{t('subs.invoiced')}</Th>
                      <Th align="end">{t('subs.collected')}</Th>
                      <Th align="end">{t('subs.outstanding')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.billing.byCurrency.map((b) => (
                      <Tr key={b.currency}>
                        <Td>{b.currency}</Td>
                        <Td align="end" className="tabular">
                          {money(b.invoiced, b.currency)}
                          <span className="block text-caption text-muted">{t('subs.invoicesCount', { count: b.invoices })}</span>
                        </Td>
                        <Td align="end" className="tabular">
                          {money(b.collected, b.currency)}
                          <span className="block text-caption text-muted">{t('subs.paymentsCount', { count: b.payments })}</span>
                        </Td>
                        <Td align="end" className="tabular">{money(b.outstanding, b.currency)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <Card padded={false}>
              <CardHeader title={t('subs.byProduct')} className="p-5 pb-0" />
              {data.byProject.length === 0 ? (
                <EmptyState />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('subs.product')}</Th>
                      <Th align="end">{t('subs.live')}</Th>
                      <Th align="end">{t('subs.kpi.mrr')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byProject.map((p) => (
                      <Tr key={p.id} onClick={() => navigate(`/subscriptions?projectId=${p.id}`)}>
                        <Td>{localized(p)}</Td>
                        <Td align="end" className="tabular">{p.live}</Td>
                        <Td align="end" className="tabular">{p.mrr.map((m) => money(m.mrr, m.currency)).join(' · ')}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
            <Card padded={false}>
              <CardHeader title={t('subs.byPackage')} className="p-5 pb-0" />
              {data.byPackage.length === 0 ? (
                <EmptyState />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('subs.package')}</Th>
                      <Th align="end">{t('subs.live')}</Th>
                      <Th align="end">{t('subs.kpi.mrr')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byPackage.map((p) => (
                      <Tr key={p.id} onClick={() => navigate(`/subscription-packages/${p.id}`)}>
                        <Td>
                          {localized(p)}
                          <span className="block text-caption text-muted">{p.project}</span>
                        </Td>
                        <Td align="end" className="tabular">{p.live}</Td>
                        <Td align="end" className="tabular">{p.mrr.map((m) => money(m.mrr, m.currency)).join(' · ')}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <Card padded={false}>
              <CardHeader title={t('subs.aging')} subtitle={t('subs.agingHint')} className="p-5 pb-0" />
              {data.aging.length === 0 ? (
                <p className="px-5 py-4 text-body-sm text-muted">{t('subs.nothingOwed')}</p>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('common.currency')}</Th>
                      {AGING_KEYS.map((k) => (
                        <Th key={k} align="end" className="whitespace-nowrap">{t(`subs.aging.${k}` as TranslationKey)}</Th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.aging.map((a) => (
                      <Tr key={a.currency}>
                        <Td>{a.currency}</Td>
                        {AGING_KEYS.map((k) => (
                          <Td key={k} align="end" className={`tabular ${k !== 'current' && a[k].amount > 0 ? 'text-warning-fg dark:text-warning-dark' : ''}`}>
                            {a[k].invoices ? money(a[k].amount, a.currency) : '—'}
                          </Td>
                        ))}
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
            <Card padded={false}>
              <CardHeader title={t('subs.trend')} className="p-5 pb-0" />
              <Table>
                <thead>
                  <tr>
                    <Th>{t('common.month')}</Th>
                    <Th align="end">{t('subs.started')}</Th>
                    <Th align="end">{t('subs.cancelledCount')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.trend.filter((m) => m.started || m.cancelled).map((m) => (
                    <Tr key={m.month}>
                      <Td>{monthLabel(m.month)}</Td>
                      <Td align="end" className="tabular">{m.started}</Td>
                      <Td align="end" className="tabular">{m.cancelled}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </div>

          <Card padded={false}>
            <CardHeader title={t('subs.upcoming')} className="p-5 pb-0" />
            {data.upcoming.length === 0 ? (
              <p className="px-5 py-4 text-body-sm text-muted">{t('subs.nothingUpcoming')}</p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('common.date')}</Th>
                    <Th>{t('subs.number')}</Th>
                    <Th>{t('subs.clientAndPackage')}</Th>
                    <Th align="end">{t('subs.netPrice')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.upcoming.map((u) => (
                    <Tr key={u.id} onClick={() => navigate(`/subscriptions/${u.id}`)}>
                      <Td className="whitespace-nowrap tabular">{formatDate(u.nextBillingDate, locale)}</Td>
                      <Td className="font-mono text-mono-sm">{u.number}</Td>
                      <Td>
                        {clientName(u.client)}
                        <span className="block text-caption text-muted">{localized(u.package)}</span>
                      </Td>
                      <Td align="end" className="tabular">{money(u.netPrice, u.currency)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------

interface BoardRow {
  id: string;
  number: string;
  status: string;
  billingCycle: Cycle;
  currency: string;
  netPrice: number;
  nextBillingDate: string | null;
  billingError: string | null;
  billingErrorAt: string | null;
  pastDueSince: string | null;
  client: { id: string; code: string; companyName: string; companyNameAr: string | null };
  project: { name: string; nameAr: string | null };
  package: { name: string; nameAr: string | null };
}

interface Board {
  today: string;
  autoBilling: boolean;
  graceDays: number;
  due: BoardRow[];
  failing: BoardRow[];
  upcoming: BoardRow[];
  pastDue: Array<BoardRow & { invoices: Array<{ id: string; number: string; dueDate: string; totalAmount: number; paidAmount: number }> }>;
  runs: Array<{
    id: string;
    trigger: 'SCHEDULE' | 'MANUAL';
    billingDate: string;
    startedAt: string;
    finishedAt: string | null;
    examined: number;
    invoicesCreated: number;
    failures: number;
  }>;
}

function BoardTable({ rows, extra }: { rows: BoardRow[]; extra?: (row: BoardRow) => ReactNode }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const clientName = useClientName();
  if (rows.length === 0) return <p className="px-5 py-4 text-body-sm text-muted">{t('subs.billing.none')}</p>;
  return (
    <Table>
      <thead>
        <tr>
          <Th>{t('subs.number')}</Th>
          <Th>{t('subs.clientAndPackage')}</Th>
          <Th className="whitespace-nowrap">{t('subs.nextBilling')}</Th>
          <Th align="end">{t('subs.netPrice')}</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <Tr key={row.id} onClick={() => navigate(`/subscriptions/${row.id}`)}>
            <Td className="whitespace-nowrap font-mono text-mono-sm text-primary-600 dark:text-primary-400">{row.number}</Td>
            <Td className="w-full min-w-[12rem] max-w-0">
              <span className="block truncate">{clientName(row.client)}</span>
              <span className="block truncate text-caption text-muted">
                {localized(row.project)} · {localized(row.package)}
              </span>
              {extra?.(row)}
            </Td>
            <Td className="whitespace-nowrap tabular text-muted">{row.nextBillingDate ? formatDate(row.nextBillingDate, locale) : '—'}</Td>
            <Td align="end" className="whitespace-nowrap tabular">{formatMoney(row.netPrice, row.currency, locale)}</Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

export function SubscriptionBillingPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscriptions', 'billing'],
    queryFn: () => api.get<Board>('/subscriptions/billing'),
  });
  const run = useMutation({
    mutationFn: () => api.post<{ skipped: string | null; run?: Board['runs'][number] }>('/subscriptions/billing/run'),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      if (result.skipped === 'RUNNING') toast.error(t('subs.billing.busy'));
      else toast.success(t('subs.billing.ran', { invoices: result.run?.invoicesCreated ?? 0, failures: result.run?.failures ?? 0 }));
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });

  return (
    <>
      <PageHeader
        title={t('subs.billing.title')}
        subtitle={t('subs.billing.subtitle')}
        action={
          can(P.SUBSCRIPTIONS_MANAGE) && (
            <Button variant="primary" icon={<Play className="h-4 w-4" />} loading={run.isPending} onClick={() => run.mutate()}>
              {t('subs.billing.run')}
            </Button>
          )
        }
      />
      {isLoading ? (
        <LoadingBlock />
      ) : error || !data ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className="grid gap-5">
          {!data.autoBilling && (
            <Card className="border-warning/40">
              <p className="text-body-sm text-warning-fg dark:text-warning-dark">{t('subs.billing.autoOff')}</p>
            </Card>
          )}
          <div className="grid gap-5 lg:grid-cols-2">
            <Card padded={false}>
              <CardHeader title={t('subs.billing.failing')} subtitle={t('subs.billing.failingHint')} className="p-5 pb-0" />
              <BoardTable
                rows={data.failing}
                extra={(row) =>
                  row.billingError && (
                    <span className="mt-1 block text-caption text-error-fg dark:text-error-dark">
                      {formatDateTime(row.billingErrorAt, locale)} — {row.billingError}
                    </span>
                  )
                }
              />
            </Card>
            <Card padded={false}>
              <CardHeader title={t('subs.billing.due')} subtitle={t('subs.billing.dueHint')} className="p-5 pb-0" />
              <BoardTable rows={data.due} />
            </Card>
          </div>
          <div className="grid gap-5 lg:grid-cols-2">
            <Card padded={false}>
              <CardHeader title={t('subs.billing.pastDue')} subtitle={t('subs.billing.pastDueHint', { days: data.graceDays })} className="p-5 pb-0" />
              <BoardTable
                rows={data.pastDue}
                extra={(row) => {
                  const invoices = data.pastDue.find((p) => p.id === row.id)?.invoices ?? [];
                  return (
                    <span className="mt-1 flex flex-wrap gap-1.5">
                      {invoices.map((i) => (
                        <Link key={i.id} to={`/invoices/${i.id}`} onClick={(e) => e.stopPropagation()}>
                          <Badge tone="warning">
                            {i.number} · {t('subs.detail.due', { date: formatDate(i.dueDate, locale) })}
                          </Badge>
                        </Link>
                      ))}
                    </span>
                  );
                }}
              />
            </Card>
            <Card padded={false}>
              <CardHeader title={t('subs.billing.upcoming')} className="p-5 pb-0" />
              <BoardTable rows={data.upcoming} />
            </Card>
          </div>
          <Card padded={false}>
            <CardHeader title={t('subs.billing.runs')} className="p-5 pb-0" />
            {data.runs.length === 0 ? (
              <p className="px-5 py-4 text-body-sm text-muted">{t('subs.billing.none')}</p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('subs.billing.startedAt')}</Th>
                    <Th>{t('common.type')}</Th>
                    <Th align="end">{t('subs.billing.examined')}</Th>
                    <Th align="end">{t('subs.billing.invoices')}</Th>
                    <Th align="end">{t('subs.billing.failures')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.runs.map((r) => (
                    <Tr key={r.id}>
                      <Td className="whitespace-nowrap tabular">{formatDateTime(r.startedAt, locale)}</Td>
                      <Td>{t(`subs.billing.trigger.${r.trigger}` as TranslationKey)}</Td>
                      <Td align="end" className="tabular">{r.examined}</Td>
                      <Td align="end" className="tabular">{r.invoicesCreated}</Td>
                      <Td align="end" className={`tabular ${r.failures ? 'text-error-fg dark:text-error-dark' : ''}`}>{r.failures}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>
      )}
    </>
  );
}
